<script lang="ts" setup>
import {
  CharacteristicType,
  CustomAccessoryArticleType,
  CustomArticleType,
  HpStep,
  InstallationType,
  KeyNumType,
  StringMap
} from '@/helper/Types'
import i18n, { formatNumber } from '@/helper/Translations'
import { StepsController } from '@/controllers/StepsController'
import projectStore from '@/store/ProjectStore'
import appStore from '@/store/AppStore'
import formStore from '@/store/FormStore'
import SummaryProduct from '@/components/steps/summary/SummaryProduct.vue'
import { ProductResponse } from '@/services/models/ProductResponse'
import userStore from '@/store/UserStore'
import { BackupHeaterCharacteristic } from '@/services/models/BackupHeaterCharacteristic'
import { DHWStorageCharacteristic } from '@/services/models/DHWStorageCharacteristic'
import { Characteristic } from '@/services/models/Characteristic'
import { ValidatorType } from '@/helper/ValidateDirective'
import FormController from '@/controllers/FormController'
import ProjectController from '@/controllers/ProjectController'
import SummaryAccessoryTable from '@/components/steps/summary/SummaryAccessoryTable.vue'
import TotalPrice from '@/components/steps/summary/TotalPrice.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import { computed, reactive, ref } from 'vue'
import SalesPackage from '@/components/steps/summary/SalesPackage.vue'
import SummaryCustomArticleTable from '@/components/steps/summary/SummaryCustomArticleTable.vue'
import { Heatpump } from '@/services/models/implementations/Heatpump.ts'
import { Product } from '@/services/models/implementations/Product.ts'
import { HPCharacteristic } from '@/services/models/HPCharacteristic.ts'
import { Article } from '@/services/models/Article.ts'
import { Role } from '@/services/models/ArticleRole.ts'

const enableCustomPrices = ref<boolean>(projectStore.projectProperties.customPrices.val !== undefined)
const validator: ValidatorType = reactive({})

const customArticlesMaterial = computed((): CustomAccessoryArticleType[] => {
  return getCustomArticlesFiltered(false)
})

const customArticlesServices = computed((): CustomAccessoryArticleType[] => {
  return getCustomArticlesFiltered(true)
})

const selectedAccessories = computed(() => {
  return projectStore.getArticlesByCharacteristic(CharacteristicType.ACCESSORY, CharacteristicType.APPLIANCE_INTERFACE)
    .filter(a => a.carryOverQuantity > 0)
})

/**
 * Getter getting converted to computed property to cache the selectedHeatPump
 * Prevent the projectStore getter being called every time projectStore.selectedHeatPump is called in template
 */
const selectedHeatPump = computed((): Article | undefined => {
  const heatPump = projectStore.getArticleByCharacteristic(CharacteristicType.HEAT_PUMP)
  return heatPump?.carryOverQuantity > 0 ? heatPump : undefined
})

const selectedComponents = computed((): Article[] => {
  const components: Article[] = projectStore.selectedArticles
    .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.HEAT_PUMP))
    .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.APPLIANCE_INTERFACE))
    .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.ACCESSORY))
    .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.SALES_PACKAGE))
    .filter(article => article.carryOverQuantity > 0)

  const articles = reduceArticles(components)

  articles.sort((a) => {
    if (a.roles && a.roles.length > 0) {
      return a.roles[0] === Role.HEATING ? -1 : 1
    }
    return -1
  })

  return articles
})

const accessoryProductsTotalPrice = computed((): number => {
  let totalPrice = 0

  selectedAccessories.value.forEach(accessory => {
    const customPrice = ((projectStore.projectProperties.customPrices.val || {}) as KeyNumType)[accessory.id]

    if (customPrice != undefined) {
      totalPrice += customPrice
    } else {
      const singlePrice = accessory.details?.price || 0
      totalPrice += singlePrice * accessory.carryOverQuantity
    }
  })

  return totalPrice
})

const customArticlesMaterialPrice = computed((): number => {
  return getCustomArticlesPriceFiltered(false)
})

const customArticlesServicesPrice = computed((): number => {
  return getCustomArticlesPriceFiltered(true)
})

function reduceArticles(articles: Article[]): Article[] {
  return Array.from(articles.reduce((accumulator, currentValue) => {
    const existing = accumulator.get(currentValue.id) && { ...accumulator.get(currentValue.id) } as Article

    if (existing) {
      existing.quantity += currentValue.quantity
    }

    return accumulator.set(currentValue.id, existing || currentValue)
  }, new Map<string, Article>).values())
}

/**
 * appStore isLoading prevents several requests which would overwrite the state and cause problems
 * These problems are caused in the customArticles since with every loadMapping new random Ids get generated
 * which might not match anymore with the customPrice ids
 */
async function customPriceBlurred() {
  if (appStore.isLoading) {
    return
  }
  appStore.isLoading = true
  await ProjectController.saveProject()
  FormController.loadFormData()
  appStore.isLoading = false
}

function enableCustomPricesChanged(): void {
  if (!enableCustomPrices.value) {
    formStore.data.customPrices = {}
    ProjectController.saveProject()
  }
}

function calculatorConfirmed(id: string, newPrice: number) {
  (formStore.data.customPrices as KeyNumType)[id] = newPrice
  customPriceBlurred()
}

function getCustomArticlesFiltered(services: boolean): CustomAccessoryArticleType[] {
  const customArticles = projectStore.projectProperties.customArticles.val as CustomArticleType[]
  const res = [] as CustomAccessoryArticleType[]

  customArticles.forEach(article => {
    if ((!services && article.group === 'services') || (services && article.group !== 'services')) {
      return
    }

    res.push({
      id: article.id,
      name: article.name,
      price: article.price,
      quantity: article.quantity,
      group: article.group
    } as CustomAccessoryArticleType)
  })

  return res
}

function getComponentSpecs(prod: ProductResponse): StringMap {
  return prod.characteristics.reduce((acc: StringMap, chr: Characteristic) => {
    const res: StringMap = acc || {}

    switch (chr.type) {
      case CharacteristicType.BUFFER_TANK:
      case CharacteristicType.COOLING_BUFFER_TANK:
      case CharacteristicType.DHW_STORAGE:
        res['volume'] =
          i18n.global.t('step_components.table_storage_volume') +
          ': ' +
          formatNumber((chr as DHWStorageCharacteristic).volume) +
          ' l'
        break
      case CharacteristicType.BACKUP_HEATER:
        res['power'] =
          i18n.global.t('step_components.table_power') +
          ': ' +
          formatNumber((chr as BackupHeaterCharacteristic).powerOutput) +
          ' kW'
        res['fuel'] =
          i18n.global.t('step_components.table_fuel_type') +
          ': ' +
          i18n.global.t('fuel_types.' + (chr as BackupHeaterCharacteristic).fuelType)
        break
    }

    return res
  }, {} as StringMap)
}

function getHpProductSpecs(product: Product): StringMap {
  const hp = product.getProductCharacteristic<HPCharacteristic>(CharacteristicType.HEAT_PUMP)

  const integratedDhwStorage: DHWStorageCharacteristic = product.getProductCharacteristic(CharacteristicType.DHW_STORAGE)
  const specs: StringMap = {}

  if (integratedDhwStorage) {
    specs['storage'] = `${i18n.global.t('step_heat_pumps.table_storage')}: ${integratedDhwStorage.volume} l`
  }

  const heating = projectStore.installationTypeName(InstallationType.HEATING)
  if (projectStore.isHeating) {
    specs['heatingPerformance'] =
      `${i18n.global.t(`components.heating_performance.${heating}`)}: ${formatNumber(projectStore.getHeatingPowerOutput(hp))} kW`

    specs['heatingCoverage'] =
      `${i18n.global.t(`components.heat_loss_coverage.${heating}`)}: ${hp.heating?.loadCoveragePercent?.toFixed(0)} %`

    specs['bivalencePoint'] =
      `${i18n.global.t('components.bivalence_point')}: ${Math.round(hp.heating?.operatingPointAtBivalencePoint?.sourceTemperature || 0)} °C`
  }

  const cooling = projectStore.installationTypeName(InstallationType.COOLING)
  if (projectStore.isCooling) {
    specs['coolingPerformance'] =
      `${i18n.global.t(`components.heating_performance.${cooling}`)}: ${formatNumber(hp.cooling?.operatingPoint.powerOutput)} kW`

    specs['coolingCoverage'] =
      `${i18n.global.t(`components.heat_loss_coverage.${cooling}`)}: ${hp.cooling?.loadCoveragePercent?.toFixed(0)} %`
  }

  return specs
}

function getCustomArticlesPriceFiltered(services: boolean): number {
  const customArticles = projectStore.projectProperties.customArticles.val as CustomArticleType[]

  let sum = 0
  customArticles.forEach(customArticle => {
    if ((!services && customArticle.group === 'services') || (services && customArticle.group !== 'services')) {
      return
    }

    const customPrice = ((projectStore.projectProperties.customPrices.val || {}) as KeyNumType)[customArticle.id]
    sum += customPrice === undefined ? (customArticle.price || 0) * customArticle.quantity : customPrice
  })
  return sum
}

function validate(): boolean {
  return true
}

defineExpose({
  validate,
  validator
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <section>
      <div class="steps-table__header">
        <SectionHeadline v-if="userStore.enableCustomPrices" translation-key="step_summary.project_settings" />
      </div>

      <div v-if="userStore.enableCustomPrices" class="summary is--settings card">
        <div class="row gh-lg-4 gh-md-2">
          <div class="col-md-6">
            <label :title="$t('step_summary.enable_custom_prices')" class="form__toggle">
              <input
                id="enable-custom-prices"
                v-model="enableCustomPrices"
                type="checkbox"
                @change="enableCustomPricesChanged"
              >

              <span class="form__toggle-text">{{ $t('step_summary.enable_custom_prices') }} *</span>
              <span class="form__tg" />
            </label>
          </div>
        </div>
      </div>

      <div v-if="userStore.enableCustomPrices" class="foot-note">
        * {{ $t('step_summary.legal_custom_price_toggle_info') }}
      </div>
    </section>

    <section v-if="projectStore.salesPackages?.length">
      <div class="steps-table__header">
        <SectionHeadline translation-key="step_summary.title_heat_pump_set" />

        <button class="item__modify btn is--raw icon-text firstfocus" @click="StepsController.jumpToStep(HpStep.HEAT_PUMP_SYSTEM)">
          <i class="ic-edit" />{{ $t('step_summary.btn_modify') }}
        </button>
      </div>

      <div class="summary card package">
        <SalesPackage
          v-for="salesPackage in projectStore.salesPackages"
          :key="salesPackage.id"
          :enable-custom-prices="enableCustomPrices"
          :sales-package="salesPackage"
          :validator="validator"
          @blur-custom-price="customPriceBlurred"
        />
      </div>
    </section>

    <section v-if="selectedHeatPump?.details">
      <div class="steps-table__header">
        <SectionHeadline translation-key="step_summary.title_heating_system" />

        <button class="item__modify btn is--raw icon-text firstfocus" @click="StepsController.jumpToStep(HpStep.HEAT_PUMP_SYSTEM)">
          <i class="ic-edit" />{{ $t('step_summary.btn_modify') }}
        </button>
      </div>

      <div class="summary is--heat-pump card">
        <SummaryProduct
          v-model:price="(formStore.data.customPrices as KeyNumType)[selectedHeatPump.id]"
          :enable-custom-prices="enableCustomPrices"
          :product="selectedHeatPump.details"
          :product-specs="getHpProductSpecs(selectedHeatPump.details as Heatpump)"
          :product-title="ProjectController.getProductTitle(selectedHeatPump.details)"
          :quantity="selectedHeatPump.carryOverQuantity"
          :validator="validator"
          product-type="heat-pump"
          @blur="customPriceBlurred"
          @confirm="newPrice => calculatorConfirmed(selectedHeatPump?.details?.id, newPrice)"
        />
      </div>
    </section>

    <section v-if="selectedComponents.length">
      <div class="steps-table__header">
        <SectionHeadline translation-key="steps.components" />

        <button class="item__modify btn is--raw icon-text firstfocus" @click="StepsController.jumpToStep(HpStep.COMPONENTS)">
          <i class="ic-edit" />{{ $t('step_summary.btn_modify') }}
        </button>
      </div>

      <div class="summary is--component card">
        <template v-for="article in selectedComponents">
          <SummaryProduct
            v-if="article.carryOverQuantity > 0"
            :key="`component-${article.id}`"
            v-model:price="(formStore.data.customPrices as KeyNumType)[article.id]"
            :enable-custom-prices="enableCustomPrices"
            :product="article.details as ProductResponse"
            :product-specs="getComponentSpecs(article.details as ProductResponse)"
            :product-title="ProjectController.getProductTitle(article.details)"
            :quantity="article.carryOverQuantity"
            :validator="validator"
            product-type="component"
            @blur="customPriceBlurred"
            @confirm="newPrice => calculatorConfirmed(article.id, newPrice)"
          />
        </template>
      </div>
    </section>

    <SummaryAccessoryTable
      :enable-custom-prices="enableCustomPrices"
      :products="selectedAccessories"
      :total-price="accessoryProductsTotalPrice"
      :total-price-label="$t('step_summary.total_accessories_price')"
      :validator="validator"
      jump-to-step="accessories"
      title="steps.accessories"
    />

    <SummaryCustomArticleTable
      :enable-custom-prices="enableCustomPrices"
      :products="customArticlesMaterial"
      :total-price="customArticlesMaterialPrice"
      :total-price-label="$t('step_summary.total_material_price')"
      :validator="validator"
      class="custom-articles-materials"
      title="step_summary.material_costs"
    />

    <SummaryCustomArticleTable
      :enable-custom-prices="enableCustomPrices"
      :products="customArticlesServices"
      :total-price="customArticlesServicesPrice"
      :total-price-label="$t('step_summary.total_services_price')"
      :validator="validator"
      class="custom-articles-services"
      title="step_summary.services"
    />

    <TotalPrice
      v-if="!userStore.hidePrices"
      :label="$t('overview.table_total_price')"
      :price="projectStore.totalPrice"
      class="card"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

$summaryPadding: 32;
$summaryPaddingMobile: 32;

:deep(.price-input__formatted-price) {
  font-weight: bold;
}

:deep(.summary) {
  margin: 0 auto;
  padding: rem-calc($summaryPadding);

  @include media('<desktop') {
    padding: rem-calc($summaryPaddingMobile);
  }
}

.item__modify {
  white-space: nowrap;
}

section:not(:first-child) {
  margin-top: 2rem;
}

.package {
  position: relative;
  overflow: visible;

  .is--package {
    &:last-child {
      margin-bottom: 0;
    }
  }
}

:deep(h2) {
  @include media('<desktop') {
    font-size: rem-calc(20);
  }

  &:not(:first-of-type) {
    margin-top: rem-calc(32);
  }
}
</style>
